import React from "react";
import PropTypes from "prop-types";
import {I18n} from "aws-amplify/utils";
import {Box, InputAdornment, List, Popper, TextField} from "@mui/material";
import {Clear, Search} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {getIconForNode} from "../../../Models/Roles";
import {filterModules, filterPayStations} from "../SearchBar/PaystationFilteringLogic";
import {getDetailedAccounts} from "../../../graphql/queries";
import GraphQlTool from "../../../Utils/GraphQlTool";
import './ModuleSelector.css';
import {boxDevices} from "../../../Models/Enums";

const defaultName = "MONNAYEUR";
/** @type {ModuleSelector} */
let selectorInstance;

/**
 * @typedef {{ id: import('../../../Models/DomainTypes').EntityId, name: string }} ModuleOption
 */

/**
 * @typedef {Object} ModuleSelectorProps
 * @property {ModuleOption[]} modules
 * @property {import('../../../Models/DomainTypes').PayStation[]} payStations
 * @property {object} user Dashboard user used to load accounts.
 * @property {number} user.accountId
 * @property {{ graphql: Function }} API
 * @property {string} [placeholder]
 * @property {boolean} [isDarkStyle]
 * @property {boolean} [showIcon]
 * @property {(event: React.MouseEvent, module: ModuleOption) => void} [onChange]
 */

/**
 * Recherche et sélectionne un module parmi ceux des installations accessibles.
 * @extends {React.Component<ModuleSelectorProps>}
 */
export default class ModuleSelector extends React.Component {
	static defaultProps = {
		placeholder: '',
		isDarkStyle: false,
		showIcon: false,
		onChange: () => {}
	};

	static propTypes = {
		placeholder: PropTypes.string.isRequired,
		isDarkStyle: PropTypes.bool.isRequired,
		showIcon: PropTypes.bool,
		onChange: PropTypes.func
	};

	constructor( props ) {
		super( props );
		selectorInstance = this;
		this.containerReference = React.createRef();
		this.listReference = React.createRef();
		this.inputReference = React.createRef();
		this.state = {
			searchValue: '',
			highlightedIndex: -1,
			isOpened: false,
			filteredModules: []
		};
	}

	loadAccounts() {
		if( this.props.API && this.props.user?.accountId ) {
			this.props.API
				.graphql( { query: getDetailedAccounts, variables: { accountId: this.props.user?.accountId }} )
				.then( returned => {
					const tool = new GraphQlTool( 'getDetailedAccounts' , returned );
					this.setState( { accounts: tool.extract() } );
				})
				.catch( ( error ) => {
					console.error( "error" , error );
				})
		}
	}

	handleClickOutOfTheZone( event ) {
		const clickedInContainer = selectorInstance.containerReference?.current?.contains( event.target );
		const clickedInList = selectorInstance.listReference?.current?.contains( event.target );
		if ( ! clickedInContainer && ! clickedInList) {
			selectorInstance.setState({ isOpened: false, highlightedIndex: -1 });
		}
	}

	clearAllFiltersIfNoSearch() {
		if( selectorInstance.state.searchValue?.trim() === '' &&
			selectorInstance.state.filteredModules !== selectorInstance.props.modules ) {
			selectorInstance.setState( {
				filteredModules: selectorInstance.props.modules,
				isOpened: false,
				highlightedIndex: -1
			} , () => { selectorInstance.inputReference?.current?.focus() } );
			return true;
		}
		return false;
	}

	/**
	 * @param {React.MouseEvent} event
	 * @param {object} module
	 * @param {import('../../../Models/DomainTypes').EntityId} module.id
	 * @param {string} module.name
	 */
	handleUserSelection( event , module ) {
		if( selectorInstance.props.onChange )
			selectorInstance.props.onChange( event , module );

		selectorInstance.setState( {
			searchValue: module.name,
			highlightedIndex: -1,
			isOpened: false
		} );
	}

	clearSearch() {
		selectorInstance.setState( { searchValue: '' } , () => { selectorInstance.inputReference?.current?.focus() } );
	}

	componentDidMount() {
		document.addEventListener( 'pointerdown', this.handleClickOutOfTheZone );
	}

	componentWillUnmount() {
		document.removeEventListener( 'pointerdown', this.handleClickOutOfTheZone );
	}

	renderSearchBar() {
		return (
			<TextField
				key={`module-selector-text-field`}
				ref={ selectorInstance.containerReference }
				inputRef={ selectorInstance.inputReference }
				label={ I18n.get('Filter by serial') }
				className="searchbar-textField"
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<Search />
						</InputAdornment>
					),
					endAdornment: (
						<InputAdornment position="end">
							<IconButton
								size="small"
								edge="end"
								onClick={ selectorInstance.clearSearch }
							>
								<Clear fontSize="small" />
							</IconButton>
						</InputAdornment>
					),
				}}
				size={ "medium" }
				variant={ "outlined" }
				placeholder={ selectorInstance.props.placeholder }
				value={ selectorInstance.state.searchValue }
				onChange={ event => selectorInstance.setState( { searchValue: event.target.value } ) }
				onFocus={ () => selectorInstance.setState( { isOpened: true } ) }
				autoComplete={ "off" }
			/>
		);
	}

	renderFiltered() {
		const popperPlacement = "bottom-start";
		const paperElevation = 3;
		const theme = selectorInstance.props.isDarkStyle ? "dark" : "";
		const classes = {
			popper: `filtered-popper`,
			paper: `filtered-popper`,
			list: `filtered-list`,
			listItem: `filtered-list-item ${theme}`,
			listItemIcon: `filtered-list-item-icon ${theme}`,
			boxTitle: `filtered-box-title ${theme}`,
			boxSubTitle: `filtered-box-subtitle ${theme}`
		};

		if( ! selectorInstance.containerReference.current ) return null;

		return (
			<Popper
				open={ selectorInstance.state.isOpened }
				anchorEl={ selectorInstance.containerReference.current }
				placement={ popperPlacement }
				className={ classes.popper } style={{ zIndex: 1500 }} >
				<Paper
					elevation={ paperElevation }
					className={ classes.paper } >
					<List
						ref={ selectorInstance.listReference }
						dense
						className={ classes.list }
						autoFocus={false}
					>
						{selectorInstance.renderModules( classes )}
					</List>
				</Paper>
			</Popper>
		);
	}

	renderModules( classes ) {
		return(
			<>
				{ selectorInstance.state.filteredModules?.map( ( module, index ) => (
					<ListItemButton
						className={ classes.listItem }
						key={ module.id }
						selected={ index === selectorInstance.state.highlightedIndex }
						autoFocus={false}
						onClick={ evt => selectorInstance.handleUserSelection( evt , module ) }
					>
						{ selectorInstance.renderOptionalIcon( classes , module ) }
						<Box>
							<div className={ classes.boxTitle }>{ module.name }</div>
						</Box>
					</ListItemButton>
				))}
			</>
		);
	}

	/**
	 * @param {object} classes
	 * @param {object} device
	 * @param {import('../../../Models/DomainTypes').EntityId} device.id
	 * @param {string} device.name
	 */
	renderOptionalIcon( classes , device ) {
		if( this.props.showIcon ) {
			return (
				<ListItemIcon className={classes.listItemIcon}>
					<FontAwesomeIcon icon={ boxDevices.getIcon( boxDevices.fromName( device.name.split("_")[1] )  ) } />
				</ListItemIcon>
			);
		}
		return null;
	}

	render() {
		if( ! this.state.accounts ) {
			this.loadAccounts();
			return null;
		}

		if( this.clearAllFiltersIfNoSearch() ) {
			return null;
		} else {
			const filtered = filterModules( this.props.modules , this.state.searchValue );
			const isFocused = document.activeElement === this.inputReference.current;
			if( ( this.state.searchValue && isFocused ) && this.state.filteredModules?.length !== filtered.length ) {
				this.setState( {
					filteredModules: filtered,
					isOpened: filtered.length > 0 /*&& isFocused*/,
					highlightedIndex: -1
				} , () => { this.inputReference?.current?.focus() } );
				return null;
			} else {
				return (
					<div>
						{ this.renderSearchBar() }
						{ this.renderFiltered() }
					</div>
				);
			}
		}
	}
}
